import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  /** More pages exist: the sentinel watches; false removes it. */
  hasMore: boolean
  /** A page is on its way: no second request until it lands. */
  loading: boolean
  onLoadMore: () => void
  /**
   * How many items are on screen. If a request ends and this did not change
   * (the page failed), the sentinel stops asking on its own and shows a retry
   * button instead of looping.
   */
  count: number
  className?: string
  /** `li` inside a list, `div` elsewhere. */
  as?: 'div' | 'li'
}

/** The nearest ancestor that scrolls, so a list inside a dialog or panel uses its own box as the root. */
const scrollParentOf = (el: HTMLElement | null): Element | null => {
  for (let node = el?.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
  }
  return null
}

/**
 * Infinite scroll, shared by every paginated list (David: no "Ver mais"). An
 * element at the end of the list asks for the next page when it comes within
 * reach of its scroll container, with a spinner while it loads.
 */
export const InfiniteSentinel = ({ hasMore, loading, onLoadMore, count, className, as = 'div' }: Props) => {
  const { t } = useTranslation('common')
  const ref = React.useRef<HTMLDivElement & HTMLLIElement>(null)
  const latest = React.useRef(onLoadMore)
  latest.current = onLoadMore
  const askedAt = React.useRef<number | null>(null)
  const [stalled, setStalled] = React.useState(false)

  /* A request that ended with no new items means it failed: stop and offer a retry. */
  React.useEffect(() => {
    if (loading || askedAt.current === null) return
    if (askedAt.current === count) setStalled(true)
    askedAt.current = null
  }, [loading, count])

  React.useEffect(() => {
    const el = ref.current
    if (!el || !hasMore || loading || stalled) return
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting) || askedAt.current !== null) return
      askedAt.current = count
      latest.current()
    }, { root: scrollParentOf(el), rootMargin: '160px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loading, stalled, count])

  if (!hasMore) return null
  const Tag = as
  return (
    <Tag ref={ref} role={as === 'li' ? 'presentation' : undefined} data-infinite-sentinel className={cn('flex justify-center py-3', className)} aria-live="polite">
      {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label={t('loading')} />}
      {!loading && stalled && (
        <button
          type="button"
          className="text-sm font-medium text-primary-700 underline-offset-2 hover:underline"
          onClick={() => {
            setStalled(false)
            askedAt.current = count
            latest.current()
          }}
        >
          {t('retry')}
        </button>
      )}
    </Tag>
  )
}
